import { deviceTypes, idFields, idTitles } from 'types/device';

/** Short labels for device status chips; keys match API `pastDueEntryTypes` (EntryTypes). */
const PAST_DUE_ENTRY_ABBREV = {
  HE: 'HE',
  /** MRI weekly entries use type `QA` in the API; UI treats them as QC. */
  QA: 'QC',
  QC: 'QC',
  VC: 'VC',
  MAM_WK: 'WK',
  MAM_MT: 'MT',
  MAM_QU: 'QU',
  MAM_CF: 'CF',
  MAM_GC: 'GC',
  RWS: 'RWS',
  RWS_SC: 'RWS-SC',
};

const normalizePastDueType = (t) => String(t ?? '').toUpperCase();

export const getPastDueEntryAbbrev = (entryType) => {
  const key = normalizePastDueType(entryType);
  return PAST_DUE_ENTRY_ABBREV[key] ?? key;
};

export const getDeviceStatusDisplayColor = (status) => {
  if (status === 'Out of Spec') return '#ff0000';
  if (status === 'Past Due Review') return '#c6823f';
  if (status === 'OK') return '#28b0aa';
  if (typeof status === 'string' && status.startsWith('Past Due ')) return '#d9a937';
  return '#000';
};

export const getDevicesForType = (devices, type) => devices.filter((device) => device.type === type);

export const getIdFieldAndTitleForType = (type) => {
  const idField = idFields[type?.toUpperCase()];
  const idTitle = idTitles[type?.toUpperCase()];

  return [idField, idTitle];
};

export const getDeviceStatus = (device, programmatic) => {
  let status = programmatic ? 'ok' : 'OK';
  const outFields = device.outFields ?? [];
  const pastDue = device.pastDueEntryTypes ?? [];

  if (outFields.length > 0) {
    status = programmatic ? 'outOfSpec' : 'Out of Spec';
  } else if (pastDue.length > 0) {
    const hasReview = pastDue.some((t) => normalizePastDueType(t) === 'REVIEW');
    const entryTypesPastDue = pastDue.filter((t) => normalizePastDueType(t) !== 'REVIEW');
    const abbrevs = [...new Set(entryTypesPastDue.map((t) => getPastDueEntryAbbrev(t)))].sort();

    if (hasReview && abbrevs.length === 0) {
      status = programmatic ? 'pastDueReview' : 'Past Due Review';
    } else if (!hasReview) {
      status = programmatic ? 'pastDueQa' : `Past Due ${abbrevs.join(', ')}`;
    } else {
      status = programmatic ? 'pastDueQa' : `Past Due ${abbrevs.join(', ')}, Review`;
    }
  }

  return status;
};

export const getDeviceIdFromUrl = (url) => {
  const urlMatches = url.match(/devices\/(.*)/);
  let deviceId = urlMatches.length > 0 ? urlMatches[1] : null;
  [deviceId] = deviceId.split('/');
  return deviceId;
};

export const getNoiseLimitsForCt = (device) => {
  if (device.type !== deviceTypes.CT) return null;

  // Calculate axial and helical limits
  const axialLimit = device.baseline.axialAvg * (1 + device.noiseRange / 100);
  const helicalLimit = device.baseline.helicalAvg * (1 + device.noiseRange / 100);

  return [axialLimit, helicalLimit];
};

export const deviceCompare = (deviceA, deviceB) => {
  const facilityA = deviceA.facilityName ? deviceA?.facilityName?.toUpperCase() : '';
  const facilityB = deviceB.facilityName ? deviceB?.facilityName?.toUpperCase() : '';
  const nameA = deviceA?.name?.toUpperCase();
  const nameB = deviceB?.name?.toUpperCase();
  if (facilityA < facilityB) {
    return -1;
  }
  if (facilityA > facilityB) {
    return 1;
  }
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  return 0;
};

export const getLatestActionForType = (device, type = 'any') => {
  if (device.actions.length === 0) {
    return {
      date: null,
      entityId: null,
      entityName: null,
    };
  }
  if (type === 'review') {
    const reviews = device.actions.filter((action) => action.type === 'REVIEW');
    return reviews[0];
  }
  const actions = device.actions.filter((action) => action.type !== 'REVIEW');
  return actions[0];
};
